import React from 'react';
import {
  useLocation,
  NavigationType,
  UNSAFE_LocationContext as LocationContext,
  type Location,
} from 'react-router-dom';
import IndexPage from 'pages/IndexPage/IndexPage';
import SubdirectoryPage from 'pages/SubdirectoryPage/SubdirectoryPage';
import SearchResultsPage from 'pages/SearchResultsPage/SearchResultsPage';
import { ROUTES } from 'constants/constants';

/** Fallback location when a modal is opened via a direct deep-link (no background). */
const DEFAULT_LIBRARY_LOCATION: Location = {
  pathname: ROUTES.library,
  search: '',
  hash: '',
  state: null,
  key: 'modal-bg-default',
};

/**
 * Renders the page a modal was launched from, so it shows (dimmed by the modal
 * backdrop) beneath the modal overlay instead of a blank backdrop.
 *
 * The launching page is captured into router state as `backgroundLocation` by
 * `useModalNavigate`. We render the page component directly and override the
 * location context (rather than `<Routes location>`): because this renders inside
 * a leaf modal route, the route's pathname base is the modal path, and
 * `<Routes location>` rejects a background location outside that base. Overriding
 * the location makes the background page's `useLocation()` / `useSearchParams()`
 * read the launching page's URL (e.g. the browse `?lineage`). Page data comes from
 * RTK Query (already cached), so no route loaders are needed here.
 */
const ModalRouteBackground: React.FC = () => {
  const location = useLocation();
  const backgroundLocation =
    (location.state as { backgroundLocation?: Location } | null)?.backgroundLocation ?? null;

  const bg = backgroundLocation ?? DEFAULT_LIBRARY_LOCATION;

  let BackgroundPage: React.FC;
  if (bg.pathname.startsWith(ROUTES.search)) BackgroundPage = SearchResultsPage;
  else if (bg.pathname.startsWith(ROUTES.browse)) BackgroundPage = SubdirectoryPage;
  else BackgroundPage = IndexPage;

  return (
    <LocationContext.Provider value={{ location: bg, navigationType: NavigationType.Pop }}>
      <BackgroundPage />
    </LocationContext.Provider>
  );
};

/**
 * Memoized so the dimmed background page renders only once while a modal is open.
 * The component takes no props, so React.memo makes it skip every parent-triggered
 * re-render (e.g. each keystroke in the modal's form) — it still re-renders when its
 * own `useLocation()` context changes (i.e. on real navigation). Without this, typing
 * in an admin modal's Name/URL fields re-rendered the entire launching page
 * (IndexPage/SubdirectoryPage/SearchResultsPage — thousands of styled elements),
 * causing ~1s of Emotion style re-serialization per keystroke.
 */
export default React.memo(ModalRouteBackground);
